import React, {
  forwardRef,
  ForwardedRef,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  RefObject,
} from 'react';
import clsx from 'clsx';
import { AriaListBoxOptions, useListBox } from 'react-aria/useListBox';
import { VirtualizerItem } from 'react-aria/private/virtualizer/VirtualizerItem';
import { useObjectRef } from 'react-aria/useObjectRef';
import { mergeProps } from 'react-aria/mergeProps';
import { ListState } from 'react-stately/useListState';
import { ReusableView } from 'react-stately/useVirtualizerState';
import { Node } from '@react-types/shared';
import { AriaListBoxProps } from '@react-types/listbox';
import styles from '@danske/sapphire-css/components/listbox/listbox.module.css';
import { ListBoxContext } from './ListBoxContext';
import { ListBoxItemSkeleton } from './ListBoxItemSkeleton';
import { ListBoxOption } from './ListBoxOption';
import { ListBoxSection } from './ListBoxSection';
import { useListWithLoadingStateLayout } from '../../utils/virtualization/useListWithLoadingStateLayout';
import { VirtualizerWithLoadingSkeleton } from '../../utils/virtualization/VirtualizerWithLoadingSkeleton';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useScrollCheck,
} from '../../utils';
import { useCollectionFocusProxy } from '../../utils/useCollectionFocusProxy';
import { tokens } from '@danske/sapphire-css/themes';
import {
  CollectionBaseKeys,
  GlobalDomAttributes,
  MultipleSelectionKeys,
} from '../../utils/types';
import { ListBoxEmptyState } from './ListBoxEmptyState';
import { useMessageFormatter } from '@react-aria/i18n';
import intlMessages from '../i18n';

/**
 * Component not exported externally which can render and manage the UI of the
 * listbox.
 * The `state` needs to be provided.
 */
export type StatelessListBoxProps<T> = Pick<
  AriaListBoxOptions<T>,
  | 'shouldUseVirtualFocus'
  | 'onAction'
  | 'autoFocus'
  | MultipleSelectionKeys
  | CollectionBaseKeys
  | 'id'
  | 'selectionMode'
> &
  Pick<AriaListBoxProps<T>, 'children'> &
  SapphireStyleProps &
  GlobalDomAttributes & {
    loadingState?: 'loading';
    loadingSkeletonRowsCount?: number;
    /**
     * Callback called when the user scrolled to the bottom of the listbox.
     * Useful when implementing infinite scroll.
     */
    onLoadMore?: () => void;
    disableSelectedStyles?: boolean;
    state: ListState<T>;
    renderLoadingSkeleton?: (index: number) => React.ReactNode;

    /**
     * Renders a message indicating the list is empty.
     * Default is a simple "No results" (in given language) message.
     * Passing `null` will hide the empty state message.
     */
    emptyState?: React.ReactNode;

    // TODO(v5): remove. Check usages in Sapphire Insights before writing a codemod.
    //  The usage is likely to be very limited if any.
    /**
     * @deprecated use marginX instead. negativeSideMargin={x} => marginX={`calc(-1 * ${x})`}
     */
    negativeSideMargin?: number | string;

    /**
     * Allows for connecting a (search) input to the ListBox, so that the ListBox
     * items can be navigated and selected by keyboard when the input is focused.
     */
    connectedInputRef?: RefObject<HTMLElement>;

    /**
     * Whether top and bottom dividers should be shown to mark ListBox's scrollable area.
     * The top divider will be shown when the list is **scrolled** down,
     * and the bottom divider will be shown when the list is **scrollable**.
     */
    hasScrollDividers?: boolean;

    /**
     * Whether the listbox items should use virtual focus instead of being focused directly.
     *
     * @default `true` if `connectedInputRef` is provided, `false` otherwise
     */
    shouldUseVirtualFocus?: boolean;
  };

const _StatelessListBox = <T extends object>(
  {
    loadingState,
    loadingSkeletonRowsCount = 4,
    state,
    renderLoadingSkeleton = (index: number) => (
      <ListBoxItemSkeleton key={index} index={index} />
    ),
    onLoadMore,
    hasScrollDividers = false,
    connectedInputRef,
    shouldUseVirtualFocus = Boolean(connectedInputRef),
    disableSelectedStyles = false,
    negativeSideMargin,
    emptyState: emptyStateProp,
    ...otherProps
  }: StatelessListBoxProps<T>,
  ref: ForwardedRef<HTMLDivElement>
): JSX.Element => {
  const { styleProps } = useSapphireStyleProps(otherProps);

  const listBoxRef = useObjectRef(ref);
  const { listBoxProps } = useListBox(
    {
      ...otherProps,
      shouldUseVirtualFocus,
      isVirtualized: true,
    },
    state,
    listBoxRef
  );

  const formatMessage = useMessageFormatter(intlMessages);

  const emptyState =
    emptyStateProp === null ? null : (
      <ListBoxEmptyState>
        {emptyStateProp || formatMessage('empty')}
      </ListBoxEmptyState>
    );

  const layout = useListWithLoadingStateLayout<T>({
    loadingState,
    loadingSkeletonRowsCount,
    sectionDividerHeight: 5,
    headingHeight: 34,
  });

  useCollectionFocusProxy({
    state,
    focusProxyRef: connectedInputRef,
    collectionRef: listBoxRef,
  });

  const { scrollCheckProps, isScrolled, isScrollable } =
    useScrollCheck(listBoxRef);

  type View = ReusableView<Node<T>, ReactNode>;

  const renderWrapper = useCallback(
    (
      parent: View | null,
      reusableView: View,
      children: View[],
      renderChildren: (views: View[]) => ReactElement[]
    ) => {
      if (reusableView.viewType === 'section' && reusableView.content) {
        return (
          <ListBoxSection
            key={reusableView.key}
            item={reusableView.content!}
            layoutInfo={reusableView.layoutInfo!}
            virtualizer={reusableView.virtualizer}
            childrenViews={children}
          >
            {renderChildren(children.filter((c) => c.viewType === 'item'))}
          </ListBoxSection>
        );
      }

      return (
        <VirtualizerItem
          key={reusableView.key}
          layoutInfo={reusableView.layoutInfo!}
          virtualizer={reusableView.virtualizer}
          parent={parent?.layoutInfo}
        >
          {reusableView.rendered}
        </VirtualizerItem>
      );
    },
    []
  );

  return (
    <ListBoxContext.Provider
      value={{
        state,
        shouldUseVirtualFocus,
        disableSelectedStyles,
        containerWidth: listBoxRef.current?.offsetWidth,
        constrainWidth:
          styleProps.style?.maxWidth !== undefined ||
          styleProps.style?.width !== undefined,
      }}
    >
      <VirtualizerWithLoadingSkeleton
        {...mergeProps(listBoxProps, scrollCheckProps)}
        onLoadMore={onLoadMore}
        scrollDirection="vertical"
        autoFocus={Boolean(otherProps.autoFocus)}
        layout={layout}
        collection={state.collection}
        persistedKeys={
          new Set(
            [
              ...state.selectionManager.selectedKeys,
              state.selectionManager.focusedKey,
            ].filter((key): key is React.Key => key !== null)
          )
        }
        className={clsx(
          styles['sapphire-listbox'],
          {
            [styles['sapphire-listbox--selection']]: !disableSelectedStyles,
          },
          styleProps.className
        )}
        style={{
          ...styleProps.style,
          padding:
            undefined /* virtualizer tries to take control of padding. this stops that */,
          ...(negativeSideMargin
            ? {
                marginLeft:
                  typeof negativeSideMargin === 'string'
                    ? `calc(${negativeSideMargin} * -1)`
                    : `-${negativeSideMargin}px`,
                marginRight:
                  typeof negativeSideMargin === 'string'
                    ? `calc(${negativeSideMargin} * -1)`
                    : `-${negativeSideMargin}px`,
              }
            : {}),
          // Should be moved to css when we are a little more sure about what we want, and specially
          // when/if we need the same feature in Sapphire Angular.
          borderTop:
            hasScrollDividers && isScrolled
              ? `${tokens.size.borderSm} solid ${tokens.color.borderSecondary}`
              : '',
          borderBottom:
            hasScrollDividers && isScrollable
              ? `${tokens.size.borderSm} solid ${tokens.color.borderSecondary}`
              : '',
        }}
        renderWrapper={renderWrapper as any}
        renderLoadingSkeleton={renderLoadingSkeleton}
        loadingSkeletonRowsCount={loadingSkeletonRowsCount}
        emptyState={emptyState}
        ref={listBoxRef}
        aria-busy={loadingState && true}
      >
        {(kind, item) => {
          const node = item as Node<T>;
          if (kind === 'item') {
            return <ListBoxOption key={node.key} item={node} />;
          }
          return null;
        }}
      </VirtualizerWithLoadingSkeleton>
    </ListBoxContext.Provider>
  );
};

export const StatelessListBox = forwardRef(_StatelessListBox) as <
  T extends object
>(
  props: StatelessListBoxProps<T> & { ref?: Ref<HTMLUListElement> }
) => React.ReactElement;
